/**
 * The only place in `src/` allowed to call the global `fetch` (#996): every
 * production fetch has a time limit, so a request that never answers cannot
 * hold a keepalive-quota charge or stall a probe loop forever. Enforced by
 * `__tests__/bareFetch996.guard.test.ts`.
 *
 * The limit covers the time until fetch settles (response headers); a caller
 * that then reads a body bounds that read itself.
 */

/** Rejection for an expired limit. Never an `AbortError`: a timeout is not a cancel. */
export class FetchTimeoutError extends Error {
  readonly timeoutMs: number;

  constructor(timeoutMs: number) {
    super(`fetch timed out after ${timeoutMs}ms`);
    this.name = 'FetchTimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

export interface FetchTimeoutOptions {
  /** Required; there is no default and no "no limit". */
  timeoutMs: number;
  /** Caller cancel: aborts the fetch, which rejects as it does for any abort (an AbortError). */
  signal?: AbortSignal;
}

/**
 * `fetch` with a mandatory time limit. NOT async on purpose: a synchronous
 * throw from `fetch` propagates synchronously (`keepaliveRequest` falls back to
 * the default transport on it). The timer is cleared and the caller-signal
 * listener removed on every settle path.
 */
export function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  { timeoutMs, signal }: FetchTimeoutOptions,
): Promise<Response> {
  const controller = new AbortController();
  const onCallerAbort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener('abort', onCallerAbort, { once: true });

  const timer = setTimeout(() => {
    // Settle first: the limit holds even if the transport ignores the abort.
    settle(() => reject(new FetchTimeoutError(timeoutMs)));
    controller.abort();
  }, timeoutMs);
  let reject: (err: unknown) => void = () => undefined;
  let resolve: (res: Response) => void = () => undefined;
  let done = false;
  const settle = (outcome: () => void) => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    signal?.removeEventListener('abort', onCallerAbort);
    outcome();
  };
  const result = new Promise<Response>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  let sent: Promise<Response>;
  try {
    sent = fetch(input, { ...init, signal: controller.signal });
  } catch (err) {
    settle(() => undefined);
    throw err;
  }
  // One hop from fetch's own settlement to ours (callers count microtasks).
  void sent.then(
    (res) => settle(() => resolve(res)),
    (err: unknown) => settle(() => reject(err)),
  );
  return result;
}
